import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import DateInput from '../components/DateInput';
import ProRataHelper from '../components/ProRataHelper';
import EmployeeCombobox from '../components/EmployeeCombobox';
import { useAuth } from '../context/AuthContext';
import { FormField, SectionLabel } from '../components/ui';

const inputCls =
  'w-full rounded-md border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 text-[14px] focus:border-brand-500 focus:ring-brand-500';

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function KV({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">{label}</p>
      <p className="text-[13.5px] font-semibold text-ink dark:text-gray-100 mt-0.5">{value || '—'}</p>
    </div>
  );
}

function PillCheckbox({ checked, onChange, label }) {
  return (
    <label
      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg ring-1 cursor-pointer transition-colors ${
        checked
          ? 'bg-brand-50 ring-brand-300 dark:bg-brand-900/20 dark:ring-brand-700'
          : 'bg-white ring-gray-200 hover:ring-brand-300 dark:bg-gray-800 dark:ring-gray-700'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
      />
      <span className="text-[13.5px] font-medium text-ink dark:text-gray-100">{label}</span>
    </label>
  );
}

function DirectionOption({ selected, onClick, title, subtitle }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`text-left px-3 py-2.5 rounded-lg ring-1 transition-colors ${
        selected
          ? 'bg-brand-50 ring-brand-300 dark:bg-brand-900/20 dark:ring-brand-700'
          : 'bg-white ring-gray-200 hover:ring-brand-300 dark:bg-gray-800 dark:ring-gray-700'
      }`}
    >
      <span className={`block text-[13.5px] font-semibold ${
        selected ? 'text-brand-800 dark:text-brand-200' : 'text-ink dark:text-gray-100'
      }`}>
        {title}
      </span>
      <span className="block text-[11.5px] text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</span>
    </button>
  );
}

export default function LeaverPage() {
  const { user } = useAuth();
  const hourlyTrackingEnabled = user?.hourly_tracking_enabled || false;
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    employee_id: '',
    leaving_date: '',
    holiday_balance_direction: 'owed_to_employee',
    holiday_days_owed: '',
    holiday_pay_owed: '',
    final_pay_checklist: {
      holiday_pay: false,
      bonus: false,
      deductions: false,
    },
    reason: 'resignation',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    api.get('/employees')
      .then(res => {
        const list = res.data.filter(e => e.status === 'active' || e.status === 'pending_start');
        setEmployees(list);
        // Pre-select the employee when arriving via /leaver?employee=...
        const preselect = searchParams.get('employee');
        if (preselect && list.find(e => e.id === preselect)) {
          setForm(prev => ({ ...prev, employee_id: preselect }));
        }
      })
      .catch(err => console.error('Failed to load employees:', err))
      .finally(() => setLoading(false));
  }, [searchParams]);

  const selectedEmp = useMemo(
    () => employees.find(e => e.id === form.employee_id) || null,
    [employees, form.employee_id]
  );

  const owedToCompany = form.holiday_balance_direction === 'owed_to_company';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const toggleChecklist = (key) => {
    setForm(prev => ({
      ...prev,
      final_pay_checklist: { ...prev.final_pay_checklist, [key]: !prev.final_pay_checklist[key] },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.employee_id) {
      setError('Please select an employee');
      return;
    }
    if (!form.leaving_date) {
      setError('Please enter a leaving date');
      return;
    }
    const leavingDate = new Date(form.leaving_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const twelveMonthsAgo = new Date(today);
    twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
    if (leavingDate < twelveMonthsAgo) {
      setError('Leaving date cannot be more than 12 months in the past');
      return;
    }

    if (selectedEmp?.status === 'pending_start') {
      if (!window.confirm(
        `${selectedEmp.first_name} ${selectedEmp.last_name} is a new starter that hasn't been submitted yet. ` +
        `Are you sure you want to record them as a leaver? Both changes will be included in your next submission.`
      )) {
        return;
      }
    }

    setSubmitting(true);
    try {
      await api.post('/pending-changes', {
        change_type: 'leaver',
        employee_id: form.employee_id,
        change_data: {
          employee_name: `${selectedEmp.first_name} ${selectedEmp.last_name}`,
          leaving_date: form.leaving_date,
          holiday_balance_direction: form.holiday_balance_direction,
          holiday_days_owed: form.holiday_days_owed,
          holiday_pay_owed: form.holiday_pay_owed,
          final_pay_checklist: form.final_pay_checklist,
          reason: form.reason,
          ...(form.reason === 'redundancy' ? {
            years_of_service: form.years_of_service,
            redundancy_pay: form.redundancy_pay,
            notice_period: form.notice_period,
          } : {}),
          ...(form.reason === 'other' ? { other_reason: form.other_reason } : {}),
          ...(form.final_hours_worked ? { final_hours_worked: form.final_hours_worked } : {}),
          notes: form.notes,
          p45_auto_issued: true,
        },
      });
      navigate('/');
    } catch (err) {
      setError('Failed to submit: ' + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading…</div>;
  }

  // KV strip values
  const empRole = selectedEmp?.job_title || '—';
  const empStarted = formatDate(selectedEmp?.start_date);
  const empPay = (() => {
    if (!selectedEmp) return '—';
    if (selectedEmp.pay_type === 'hourly' && selectedEmp.hourly_rate) {
      return `£${parseFloat(selectedEmp.hourly_rate).toFixed(2)} / hr`;
    }
    if (selectedEmp.annual_salary) {
      return `£${Number(selectedEmp.annual_salary).toLocaleString('en-GB')} / yr`;
    }
    return '—';
  })();
  const empHours = (() => {
    if (!selectedEmp) return '—';
    if (selectedEmp.contracted_hours_per_week != null && selectedEmp.contracted_hours_per_week !== '') {
      return `${selectedEmp.contracted_hours_per_week} hrs`;
    }
    return selectedEmp.pay_type === 'hourly' ? 'Hourly' : 'Salaried';
  })();

  return (
    <div className="max-w-[820px] mx-auto px-8 py-7">
      <button
        type="button"
        onClick={() => navigate('/')}
        className="text-[13px] text-brand-700 hover:text-brand-800 font-medium inline-flex items-center gap-1 mb-3"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M15 18l-6-6 6-6" />
        </svg>
        Back to dashboard
      </button>

      <header className="mb-6">
        <p className="text-[12px] uppercase tracking-[0.14em] text-rose-700 font-bold mb-1">Record a leaver</p>
        <h1 className="font-heading text-[28px] text-ink dark:text-gray-100 leading-none">Who's leaving?</h1>
      </header>

      {error && (
        <div className="mb-4 p-3 bg-rose-50 ring-1 ring-rose-200 rounded-lg text-sm text-rose-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Card 1 — Employee picker + preview */}
        <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 p-5 mb-4">
          <FormField label="Select employee" htmlFor="employee_id">
            <EmployeeCombobox
              id="employee_id"
              employees={employees}
              value={form.employee_id}
              onChange={(empId) => { setForm(prev => ({ ...prev, employee_id: empId })); setError(''); }}
            />
          </FormField>

          {selectedEmp && (
            <>
              {selectedEmp.status === 'pending_start' && (
                <div className="mt-4 p-3 bg-amber-50 ring-1 ring-amber-200 rounded-lg text-[13px] text-amber-800">
                  This employee is a new starter that hasn't been submitted yet. Recording them as a leaver will
                  include both changes in your next submission.
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 grid grid-cols-2 sm:grid-cols-4 gap-4">
                <KV label="Role" value={empRole} />
                <KV label="Started" value={empStarted} />
                <KV label="Pay" value={empPay} />
                <KV label="Hours/wk" value={empHours} />
              </div>
            </>
          )}
        </div>

        {/* Card 2 — Leaving details */}
        <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 p-5 mb-4">
          <SectionLabel>Leaving details</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField label="Last working day" htmlFor="leaving_date">
              <DateInput
                name="leaving_date"
                value={form.leaving_date}
                onChange={handleChange}
                className={inputCls}
              />
            </FormField>
            <FormField label="Reason" htmlFor="reason">
              <select
                id="reason"
                name="reason"
                value={form.reason}
                onChange={handleChange}
                className={inputCls}
              >
                <option value="resignation">Resignation</option>
                <option value="redundancy">Redundancy</option>
                <option value="retirement">Retirement</option>
                <option value="dismissal">Dismissal</option>
                <option value="end-of-contract">End of contract</option>
                <option value="other">Other</option>
              </select>
            </FormField>
          </div>

          {/* Holiday balance — direction toggle + figures */}
          <div>
            <p className="block text-[12px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">
              Outstanding holiday
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-1.5">
              <DirectionOption
                selected={form.holiday_balance_direction === 'owed_to_employee'}
                onClick={() => setForm(prev => ({ ...prev, holiday_balance_direction: 'owed_to_employee' }))}
                title="Owed to employee"
                subtitle="Untaken holiday — paid out"
              />
              <DirectionOption
                selected={form.holiday_balance_direction === 'owed_to_company'}
                onClick={() => setForm(prev => ({ ...prev, holiday_balance_direction: 'owed_to_company' }))}
                title="Owed back to company"
                subtitle="Overtaken holiday — deducted"
              />
            </div>
            <p className="text-[11.5px] text-gray-400 mb-3">
              {form.holiday_balance_direction === 'owed_to_employee'
                ? 'The employee has holiday left over. It will be added to their final pay.'
                : 'The employee has taken more holiday than they had accrued. The overpayment will be deducted from their final pay.'}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                label={owedToCompany ? 'Holiday days overtaken' : 'Holiday days owed'}
                htmlFor="holiday_days_owed"
                hint={owedToCompany ? 'Days taken beyond the accrued entitlement.' : 'Untaken days to be paid out.'}
              >
                <input
                  id="holiday_days_owed"
                  name="holiday_days_owed"
                  type="number"
                  step="0.5"
                  min="0"
                  placeholder="0"
                  value={form.holiday_days_owed}
                  onChange={handleChange}
                  className={`${inputCls} tabular-nums`}
                />
              </FormField>
              <FormField
                label={owedToCompany ? 'Holiday pay to deduct' : 'Holiday pay owed'}
                htmlFor="holiday_pay_owed"
                hint={owedToCompany
                  ? 'Amount to claw back from final pay. Leave blank to let your accountant calculate it.'
                  : 'Leave blank to let your accountant calculate it.'}
              >
                <div className="flex">
                  <span className="px-3 py-2 rounded-l-md bg-gray-50 dark:bg-gray-900 ring-1 ring-gray-200 dark:ring-gray-700 text-[13px] text-gray-500 dark:text-gray-400 inline-flex items-center">
                    {owedToCompany ? '−£' : '£'}
                  </span>
                  <input
                    id="holiday_pay_owed"
                    name="holiday_pay_owed"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={form.holiday_pay_owed}
                    onChange={handleChange}
                    className={`flex-1 rounded-l-none ${inputCls} tabular-nums`}
                  />
                </div>
              </FormField>
            </div>
          </div>

          {form.reason === 'other' && (
            <div className="mt-4">
              <FormField label="Please specify the reason for leaving" htmlFor="other_reason">
                <input
                  id="other_reason"
                  name="other_reason"
                  type="text"
                  value={form.other_reason || ''}
                  onChange={handleChange}
                  placeholder="e.g. TUPE transfer, mutual agreement"
                  className={inputCls}
                />
              </FormField>
            </div>
          )}

          {form.reason === 'redundancy' && (
            <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/10 ring-1 ring-amber-200 dark:ring-amber-800 rounded-lg space-y-3">
              <p className="text-[12px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300">Redundancy details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField label="Years of service" htmlFor="years_of_service">
                  <input
                    id="years_of_service"
                    name="years_of_service"
                    type="number"
                    min="0"
                    step="1"
                    value={form.years_of_service || ''}
                    onChange={handleChange}
                    className={`${inputCls} tabular-nums`}
                  />
                </FormField>
                <FormField label="Redundancy pay (£)" htmlFor="redundancy_pay">
                  <input
                    id="redundancy_pay"
                    name="redundancy_pay"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.redundancy_pay || ''}
                    onChange={handleChange}
                    placeholder="Statutory or enhanced"
                    className={`${inputCls} tabular-nums`}
                  />
                </FormField>
              </div>
              <FormField label="Notice period details" htmlFor="notice_period">
                <input
                  id="notice_period"
                  name="notice_period"
                  type="text"
                  value={form.notice_period || ''}
                  onChange={handleChange}
                  placeholder="e.g. 3 months, paid in lieu"
                  className={inputCls}
                />
              </FormField>
            </div>
          )}

          {hourlyTrackingEnabled && selectedEmp?.pay_type === 'hourly' && (
            <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/10 ring-1 ring-amber-200 dark:ring-amber-800 rounded-lg space-y-3">
              <p className="text-[12px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300">Hourly employee — final period</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField label="Hours worked in final period" htmlFor="final_hours_worked">
                  <input
                    id="final_hours_worked"
                    name="final_hours_worked"
                    type="number"
                    step="0.5"
                    min="0"
                    value={form.final_hours_worked || ''}
                    onChange={handleChange}
                    placeholder="e.g. 32"
                    className={`${inputCls} tabular-nums`}
                  />
                </FormField>
                <FormField label="Hourly rate">
                  <p className="py-2 text-[14px] text-ink dark:text-gray-100 tabular-nums">
                    £{parseFloat(selectedEmp.hourly_rate || 0).toFixed(2)} / hr
                  </p>
                </FormField>
              </div>
              {form.final_hours_worked && selectedEmp.hourly_rate && (
                <p className="text-[13px] text-amber-800 dark:text-amber-300">
                  Estimated final period gross:{' '}
                  <span className="font-semibold tabular-nums">
                    £{(parseFloat(form.final_hours_worked) * parseFloat(selectedEmp.hourly_rate)).toFixed(2)}
                  </span>
                </p>
              )}
            </div>
          )}

          {selectedEmp && selectedEmp.pay_type !== 'hourly' && form.leaving_date && selectedEmp.annual_salary && (
            <div className="mt-4">
              <ProRataHelper
                annualSalary={selectedEmp.annual_salary}
                employmentEnd={form.leaving_date}
                initialMethod={selectedEmp.prorata_method || undefined}
                label="Final-month pro-rata pay"
              />
            </div>
          )}
        </div>

        {/* Card 3 — Final pay adjustments */}
        <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 p-5 mb-4">
          <SectionLabel>Final pay adjustments</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <PillCheckbox
              checked={form.final_pay_checklist.holiday_pay}
              onChange={() => toggleChecklist('holiday_pay')}
              label="Holiday pay"
            />
            <PillCheckbox
              checked={form.final_pay_checklist.bonus}
              onChange={() => toggleChecklist('bonus')}
              label="Bonus"
            />
            <PillCheckbox
              checked={form.final_pay_checklist.deductions}
              onChange={() => toggleChecklist('deductions')}
              label="Deductions"
            />
          </div>
          <FormField label="Notes for accountant" htmlFor="notes">
            <textarea
              id="notes"
              name="notes"
              rows={3}
              value={form.notes}
              onChange={handleChange}
              placeholder="e.g. holiday pay £500, bonus £1,000, loan deduction £200"
              className={`${inputCls} text-[13.5px]`}
            />
          </FormField>
        </div>

        {/* P45 info banner */}
        <div className="bg-sky-50 dark:bg-sky-900/20 ring-1 ring-sky-200 dark:ring-sky-800 rounded-xl p-4 mb-5 flex items-start gap-3">
          <svg
            className="text-sky-600 dark:text-sky-400 mt-0.5 flex-shrink-0"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4" />
            <circle cx="12" cy="16" r="0.5" />
          </svg>
          <div>
            <p className="text-[13.5px] font-semibold text-sky-900 dark:text-sky-100">A P45 will be issued automatically</p>
            <p className="text-[12.5px] text-sky-800 dark:text-sky-300 mt-0.5">
              Once your accountant processes this period's payroll, the leaver will receive their P45 with their final payslip.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="px-5 py-2.5 rounded-md text-[14px] font-medium text-gray-700 dark:text-gray-200 ring-1 ring-gray-200 dark:ring-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-5 py-2.5 rounded-md text-[14px] font-semibold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Recording…' : 'Record leaver'}
          </button>
        </div>
      </form>
    </div>
  );
}
